import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { AuthData, toUser, getAccessToken } from "@/shared/types/auth";
import { clearCachedLawyerStatus } from "./useIsLawyer";

export function useAuth(): AuthData {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["auth", "user"],
    queryFn: async () => {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      return toUser(session?.user || null);
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
  });

  const signOutMutation = useMutation({
    mutationFn: async () => {
      // Clear lawyer status cache before signing out
      clearCachedLawyerStatus();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate auth query to clear user data
      queryClient.setQueryData(["auth", "user"], null);
      // Also invalidate user data queries
      queryClient.invalidateQueries({ queryKey: ["userData"] });
      queryClient.invalidateQueries({ queryKey: ["deceasedUserData"] });
    },
  });

  const signOut = async () => {
    await signOutMutation.mutateAsync();
  };

  return {
    user: user || null,
    loading,
    error: error as Error | null,
    signOut,
    refetch,
    getAccessToken,
  };
}
